import { useCallback } from 'react';
import { useOpenModal } from 'src/common/components/next-modal';
import { request } from 'src/common/request';
import { AppealResult } from 'src/components/share/appeal-result';
import { ShareAudit } from 'src/components/share/share-audit';
import { useGetPageId } from 'src/utils/getPageId';

import { BuildInEmail, FlowUsEmail } from '@flowus/common/const';
import { getCurrentUser } from '../user';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export const useReportIllegal = (
  uuid?: string,
  opt?: {
    blockType?: 'block' | 'form';
  }
) => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  const openReportIllegal = useOpenReportIllegal(uuid, opt);

  return useCallback(async () => {
    if (__BUILD_IN__) {
      openModal.warning({
        title: t('hooks.report_illegal.report'),
        colorType: 'active',
        content: (
          <div>
            {t('hooks.report_illegal.quick_report_email')}
            <a
              className="animate-click underline underline-offset-4"
              href={`mailto:${BuildInEmail}`}
            >
              {BuildInEmail}
            </a>
          </div>
        ),
      });
      return;
    }

    openModal.warning({
      title: t('hooks.report_illegal.report_appeal'),
      minWidth: 350,
      content: (
        <div className="space-y-2">
          <div>{t('hooks.report_illegal.fraud_warning')}</div>
          <div>
            {t('hooks.report_illegal.quick_report_email')}
            <a
              className="animate-click underline underline-offset-4"
              href={`mailto:${FlowUsEmail}`}
            >
              {FlowUsEmail}
            </a>
          </div>
        </div>
      ),
      colorType: 'active',
      confirmText: t('hooks.report_illegal.online_report_appeal'),
      confirm: openReportIllegal,
    });
  }, [openModal, openReportIllegal, t]);
};

const useOpenReportIllegal = (
  uuid?: string,
  opt?: {
    blockType?: 'block' | 'form';
  }
) => {
  const openModal = useOpenModal();
  const pageId = useGetPageId();

  return useCallback(async () => {
    // https://dev.allflow.cn/swagger/#/editor/editor.shareCheck
    const ShareCheckType = {
      report: 'complain', // 举报
      justice: 'appeal', // 申诉
    };

    const canReport = getCurrentUser().uuid
      ? await request.editor.shareCheck(uuid || pageId, ShareCheckType.report)
      : true;

    if (canReport === false) {
      openModal.modal({
        content: ({ onCloseModal: onClose }) => (
          <AppealResult success={false} type="report" onCloseModal={onClose} />
        ),
      });
    } else {
      openModal.modal({
        content: ({ onCloseModal }) => (
          <ShareAudit
            blockType={opt?.blockType}
            type="report"
            uuid={uuid || pageId}
            onCloseModal={onCloseModal}
          />
        ),
      });
    }
  }, [openModal, opt?.blockType, pageId, uuid]);
};
